import React, { ReactNode, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import CustomModal from "shared/components/modal/CustomModal";
import ScrollElement from "shared/components/scrollElement/ScrollElement";
import TextInput from "shared/components/input/TextInput";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AiFillMinusCircle } from "react-icons/ai";
import TelefoneInput from "shared/components/input/TelefoneInput";
import CustomButton from "shared/components/button/CustomButton";
import { EmailDTO } from "types/dto/EmailDTO";
import { TelefoneDTO } from "types/dto/TelefoneDTO";
import adicionarContatosSchema from "shared/yup/schemas/AdicionarContatosSchema";
import { useGestaoCobrancaCadastroContext } from "context/gestaoCobranca/GestaoCobrancaCadastroContext";
import { ContatosGestaoCobrancaDTO } from "types/dto/ContatosGestaoCobrancaDTO";
import { removerCaracteresEspeciais } from "shared/util/functions/formatacoesDeValores/FormatarValores";
import { usePostInformacoesUnidade } from "hooks/unidade/usePostInformacoesUnidade";
import showAlert from "shared/util/functions/alert/showAlert";
import { toast } from "react-hot-toast";
import { Tooltip } from "antd";

interface ModalAdicionarContatosProps {
  showModal: boolean;
  onCloseModal: () => void;
  contato: ContatosGestaoCobrancaDTO | null | undefined;
}

export interface AdicionarContatosForm {
  emails: EmailDTO[];
  telefonesProprietario: TelefoneDTO[];
}

const ModalAdicionarContatos: React.FC<ModalAdicionarContatosProps> = (props) => {
  const { unidadeId } = useGestaoCobrancaCadastroContext();

  const { mutateAsync } = usePostInformacoesUnidade();

  const { register, control, handleSubmit, setValue, reset, formState } =
    useForm<AdicionarContatosForm>({
      resolver: yupResolver<AdicionarContatosForm>(adicionarContatosSchema),
    });

  const {
    fields: emails,
    append: adicionarEmail,
    remove: removerEmail,
  } = useFieldArray({
    control,
    name: "emails",
  });

  const {
    fields: telefones,
    append: adicionarTelefone,
    remove: removerTelefone,
  } = useFieldArray({
    control,
    name: "telefonesProprietario",
  });

  useEffect(() => {
    if (props.showModal) {
      setarDefaultValues();
    }
  }, [props.showModal]);

  const setarDefaultValues = () => {
    setValue("emails", getDefaultValuesEmails());
    if (props.contato?.proprietarioId) {
      setValue("telefonesProprietario", getDefaultValuesTelefones());
    }
  };

  const getDefaultValuesEmails = (): EmailDTO[] => {
    if (props?.contato?.emails && props.contato.emails.length > 0) {
      return props?.contato.emails;
    }
    return [];
  };

  const getDefaultValuesTelefones = (): TelefoneDTO[] => {
    if (props?.contato?.telefonesProprietario && props.contato.telefonesProprietario.length > 0) {
      return props.contato.telefonesProprietario;
    }
    return [];
  };

  const renderBodyAdicionarContato = () => {
    return (
      <Row>
        <Col xs={6}>
          <h3 className="sub-titulo mb-1">E-mail</h3>
          <ScrollElement height="23vh">{renderEmails()}</ScrollElement>
          <span onClick={() => adicionarNovoEmail()} className="mt-2 blue-label-gc">
            + Adicionar email
          </span>
        </Col>

        {props?.contato?.proprietarioId && (
          <Col xs={6}>
            <h3 className="sub-titulo  mb-1">Telefone</h3>
            <ScrollElement height="23vh">{renderTelefones()}</ScrollElement>
            <span onClick={() => adicionarNovoTelefone()} className="mt-1 blue-label-gc">
              + Adicionar telefone
            </span>
          </Col>
        )}
      </Row>
    );
  };

  const renderEmails = (): ReactNode => {
    if (emails.length > 0) {
      return emails.map((email: EmailDTO, index: number) => {
        return (
          <Row key={email.id} className="mb-2 me-0">
            <Col xs={10} className="pe-0">
              <TextInput
                id={`emails.${index}.email`}
                usarValidacaoParaFormulario
                setValue={setValue}
                control={control}
                name={`emails.${index}.email`}
                register={register}
              />
            </Col>
            <Col xs={1} className="pe-0 mt-2">
              <Tooltip title="Excluir e-mail" placement="top">
                <a>
                  <AiFillMinusCircle
                    onClick={() => removerEmail(index)}
                    className="cursor-pointer"
                    color="#a498e7"
                  />
                </a>
              </Tooltip>
            </Col>
          </Row>
        );
      });
    }

    return <span>-</span>;
  };

  const renderTelefones = (): ReactNode => {
    if (telefones.length > 0) {
      return telefones.map((telefone: TelefoneDTO, index: number) => {
        return (
          <Row key={telefone.id} className="mb-2 me-0">
            <Col xs={10} className="pe-0">
              <TelefoneInput
                id={`telefonesProprietario.${index}.numeroFormatado`}
                usarValidacaoParaFormulario
                register={register}
                control={control}
                name={`telefonesProprietario.${index}.numeroFormatado`}
                setValue={setValue}
              />
            </Col>
            <Col xs={1} className="pe-0 mt-2">
              <Tooltip title="Excluir telefone" placement="top">
                <a>
                  <AiFillMinusCircle
                    onClick={() => removerTelefone(index)}
                    className="cursor-pointer"
                    color="#a498e7"
                  />
                </a>
              </Tooltip>
            </Col>
          </Row>
        );
      });
    }

    return <span>-</span>;
  };

  const adicionarNovoTelefone = () => {
    adicionarTelefone({ numeroFormatado: "" });
  };

  const adicionarNovoEmail = () => {
    adicionarEmail({ email: "" });
  };

  const renderFooterModalAdicionarContato = (): ReactNode => {
    return (
      <Row>
        <Col className="d-flex justify-content-end p-0">
          <CustomButton
            id="btn-cancelar-modal-registrar-contato"
            label="Cancelar"
            onClick={() => closeModal()}
            variant="danger-alt"
            className="me-2"
          />
          <CustomButton
            id="btn-cancelar-modal-registrar-contato"
            label="Salvar"
            onClick={handleSubmit(onSubmitRegistrarContatoRealizado)}
            variant="primary"
          />
        </Col>
      </Row>
    );
  };

  const onSubmitRegistrarContatoRealizado = async (contatos: AdicionarContatosForm) => {
    if (formState.isDirty) {
      const adicionarContatosDTO = {
        emails: preencherEmails(contatos.emails),
        telefonesProprietario: preencherTelefones(contatos.telefonesProprietario),
        proprietarioId: props?.contato?.proprietarioId,
        unidadeId,
        isEmailsDoProprietario: props?.contato?.isEmailsDoProprietario,
      } as ContatosGestaoCobrancaDTO;

      try {
        await mutateAsync(adicionarContatosDTO);
        reset();
        props.onCloseModal();
      } catch (error: any) {
        if (error?.response?.data?.validation[0]?.message) {
          toast.error(error.response.data.validation[0].message);
        } else {
          toast.error("Erro ao salvar registro.");
        }
      }
    } else {
      props.onCloseModal();
    }
  };

  const closeModal = () => {
    const fecharModal = () => {
      reset();
      props.onCloseModal();
    };
    if (formState.isDirty) {
      showAlert({
        texto:
          "Existem alterações que não foram salvas na tela. Tem certeza de que deseja sair sem salvar?",
        tipoAlerta: "CONFIRMAR_CANCELAR_ALERT",
        onConfirm: () => fecharModal(),
      });
    } else {
      fecharModal();
    }
  };

  const preencherEmails = (emails: EmailDTO[]) => {
    return emails.map((email: EmailDTO, index: number) => {
      return {
        ...email,
        principal: index === 0 ? true : false,
      };
    });
  };

  const preencherTelefones = (telefones: TelefoneDTO[]): TelefoneDTO[] => {
    return telefones.map((telefone: TelefoneDTO, index: number) => {
      let tel = removerCaracteresEspeciais(telefone.numeroFormatado ?? "");
      let indexOfBraces = telefone.numeroFormatado?.indexOf(")");
      let numeroFormatado =
        telefone.numeroFormatado?.substring(0, indexOfBraces! + 1) +
        " " +
        telefone.numeroFormatado?.substring(indexOfBraces! + 1);
      return {
        ...telefone,
        telefone: tel,
        numeroFormatado,
        principal: index === 0 ? true : false,
      };
    });
  };

  return (
    <CustomModal
      titulo="Adicionar contato"
      onCloseModal={() => closeModal()}
      customFooter={renderFooterModalAdicionarContato()}
      showModal={props.showModal}
      size="xl"
      tipoModal="MODAL_LATERAL_METADE"
      fecharModalAoClicarFora={!formState.isDirty}
      scrollable
    >
      {renderBodyAdicionarContato()}
    </CustomModal>
  );
};

export default ModalAdicionarContatos;
